// noinspection SpellCheckingInspection

import React, {useEffect, useRef, useState, useMemo} from "react"
import {
    getInspections,
    getUsers,
    getVehicles,
    postInspection,
    updateInspection,
} from "../api/dataService"
import {
    InitialVehicleInspectionObject,
    InspectionVehicleConditionArray,
    InspectionVehicleLeakArray,
    PartialUser,
    PartialVehicleInspection,
    UserRoles,
    Vehicle,
    VehicleInspection,
} from "../types"
import {checkIsAllowed, handlePostError} from "../util/Helpers"
import {Alert, Button, Col, Form, Row, Tab, Tabs} from "react-bootstrap"
import * as Yup from 'yup'
import {ErrorMessage, Field, Formik} from "formik"
import Container from "react-bootstrap/Container"
import Select from "react-select"
import {format} from "date-fns"
import DatePicker from "react-datepicker"
import {useSelector} from "react-redux"
import {RootState} from "../redux/reduxTypes"
import {Navigate} from "react-router-dom"

interface CreateInspectionEntryProps {
    selectedEmployee?: PartialUser
    inspectionToUpdate?: PartialVehicleInspection
    closeModal?: () => void
    onCreate?: (created: VehicleInspection) => void
    onUpdate?: (updated: any) => void
}

type InspectionFormValues = Omit<PartialVehicleInspection, 'employee' | 'vehicle'> & {
    employee: string | number | ''
    vehicle: string | number | ''
}

const CreateInspectionEntry: React.FC<CreateInspectionEntryProps> = (props) => {
    const user = useSelector((state: RootState) => state.user)
    const allowedToUpdate = checkIsAllowed(user.groups, [UserRoles.ownership, UserRoles.management, UserRoles.officeAdmin])
    const isUpdating = !!props.inspectionToUpdate

    const [errorMessages, setErrorMessages] = useState<string[]>([])
    const [showSuccess, setShowSuccess] = useState(false)
    const [redirectPath, setRedirectPath] = useState<string | null>(null)
    const formikRef = useRef<any>(null)
    const vehicleSelectRef = useRef<any>(null)
    const fluidSelectRef = useRef<any>(null)
    const conditionSelectRef = useRef<any>(null)
    const [employeeList, setEmployeeList] = useState<PartialUser[]>([])
    const [vehicleList, setVehicleList] = useState<Vehicle[]>([])
    const [inspectionCompletedChecked, setInspectionCompletedChecked] = useState(false)

    const [odoStart, setOdoStart] = useState<string>(
        props.inspectionToUpdate?.odometer_start ? props.inspectionToUpdate.odometer_start.toString() : ''
    )
    const [odoEnd, setOdoEnd] = useState<string>(
        props.inspectionToUpdate?.odometer_end ? props.inspectionToUpdate.odometer_end.toString() : ''
    )

    useEffect(() => {
        Promise.all([getVehicles()])
            .then(([vehicleResponse]) => {
                if ('redirectTo' in vehicleResponse) {
                    setRedirectPath(vehicleResponse.redirectTo)
                    return
                }
                setVehicleList(Array.isArray(vehicleResponse) ? vehicleResponse : [])
            })
            .catch(err => console.error('Error fetching vehicles:', err))
    }, [])

    useEffect(() => {
        if (allowedToUpdate) {
            getUsers(undefined, '?is_active=true')
                .then(res => {
                    if ('redirectTo' in res) { setRedirectPath(res.redirectTo); return }
                    setEmployeeList(Array.isArray(res) ? res : [])
                })
                .catch(err => console.error('Error fetching users:', err))
        } else if (user?.userid) {
            getUsers(user.userid)
                .then(res => {
                    if ('redirectTo' in res) { setRedirectPath(res.redirectTo); return }
                    if (!Array.isArray(res)) {
                        setEmployeeList([res])
                        formikRef.current?.setFieldValue('employee', res.id)
                    }
                })
                .catch(err => console.error('Error fetching user:', err))
        }
    }, [allowedToUpdate, user])

    useEffect(() => {
        if (props.selectedEmployee && formikRef.current) {
            formikRef.current.setFieldValue('employee', props.selectedEmployee.id)
        }
    }, [props.selectedEmployee])

    const validationSchema = Yup.object().shape({
        employee: Yup.string().required('An employee is required.'),
        vehicle: Yup.string().required('A vehicle must be selected.'),
        inspection_date: Yup.date().required('Inspection date is required.'),
        odometer_start: Yup.number().required('Odometer Start is required.'),
        odometer_end: Yup.number().required('Odometer End is required.'),
    })

    const initialValues: InspectionFormValues = useMemo(() => {
        if (props.inspectionToUpdate) {
            return {
                ...props.inspectionToUpdate,
                employee: props.inspectionToUpdate.employee?.id ?? '',
                vehicle: props.inspectionToUpdate.vehicle?.id ?? '',
            }
        }
        const now = new Date()
        now.setSeconds(0, 0)
        now.setMinutes(Math.floor(now.getMinutes() / 15) * 15)
        return {
            ...InitialVehicleInspectionObject,
            inspection_date: format(now, 'yyyy-MM-dd HH:mm:ss'),
            employee: props.selectedEmployee?.id ?? user.userid ?? '',
            vehicle: '',
        }
    }, [props.inspectionToUpdate, props.selectedEmployee?.id, user.userid])

    const getComponentsRequiringAttention = (v: InspectionFormValues) => {
        const items: string[] = []
        if (!v.pre_job_inspection_oil) items.push('Oil Needs Attention')
        if (!v.pre_job_inspection_antifreeze) items.push('Antifreeze Needs Attention')
        if (!v.pre_job_inspection_power_steering) items.push('Power Steering Fluid Needs Attention')
        if (!v.pre_job_inspection_brake) items.push('Brake Fluid Needs Attention')
        if (!v.pre_job_inspection_windshield_washer) items.push('Washer Fluid Needs Attention')
        if (!v.pre_job_inspection_tires_proper_inflation) items.push('Tire Inflation Needs Attention')
        if (!v.pre_job_inspection_tires_adaquate_tread) items.push('Tire Tread Needs Attention')
        if (!v.pre_job_inspection_headlights) items.push('Headlights need attention')
        if (!v.pre_job_inspection_brakelights) items.push('Brakelights need attention')
        if (!v.pre_job_inspection_turnlights) items.push('Turn Signals need attention')
        if (!v.pre_job_inspection_hazzardlights) items.push('Hazard Lights need attention')
        if (!v.pre_job_inspection_firstaidkit) items.push('First Aid Kit needs attention')
        if (!v.pre_job_inspection_fireextinguisher) items.push('Fire Extinguisher needs attention')
        if (!v.pre_job_inspection_reflectors) items.push('Reflectors need attention')
        if (!v.pre_job_inspection_reflectivetriangles) items.push('Reflective Triangles Missing')
        if (!v.pre_job_inspection_air_system_inspected_and_bled) items.push('Air System needs attention')
        if (!v.pre_job_inspection_frame_and_suspension_system) items.push('Frame / Suspension needs attention')
        if (!v.pre_job_inspection_fuel_system) items.push('Fuel System needs attention')
        if (!v.pre_job_inspection_temperature) items.push('Temperature Sensor needs attention')
        if (!v.pre_job_inspection_seat_belts) items.push('Seat Belts need attention')
        if (!v.pre_job_inspection_two_way_radio_in_working_order) items.push('Two Way Radio needs attention')
        if (!v.pre_job_inspection_noise) items.push('Vehicle Making Abnormal Noise')
        if (!v.pre_job_inspection_dashboard_warning_light) items.push('Vehicle Has Dashboard Warning Lights')
        if (!v.pre_job_inspection_windshield_wipers) items.push('Windshield Wipers need attention')
        if (!v.pre_job_inspection_heater_and_defroster) items.push('Heater / Defroster needs attention')
        if (!v.pre_job_inspection_mirrors) items.push('Mirrors need attention')
        if (!v.pre_job_inspection_windshield_and_perimeter_windows) items.push('Windshield / Perimeter Windows need attention')
        if (!v.pre_job_inspection_horn) items.push('Horn needs attention')
        if (!v.pre_job_inspection_exhaust_system) items.push('Exhaust System needs attention')
        if (!v.pre_job_inspection_brakes_including_parking_brake) items.push('Brakes (Incl. Parking Brake) need attention')
        return items
    }

    const handleSubmit = async (values: InspectionFormValues) => {
        try {
            const data: any = { ...values }

            let response: any
            if (isUpdating) {
                response = await updateInspection(props.inspectionToUpdate!.id!.toString(), data)
            } else {
                response = await postInspection(data)
            }

            if (response && !Array.isArray(response)) {
                setErrorMessages([])
                setShowSuccess(true)
                formikRef.current?.resetForm()
                setInspectionCompletedChecked(false)

                if (!isUpdating && props.onCreate) {
                    const created = response.data
                    created.employee = employeeList.find(e => String(e.id) === String(values.employee))
                    created.vehicle = vehicleList.find(v => String(v.id) === String(values.vehicle))
                    props.onCreate(created)
                }

                if (isUpdating && props.onUpdate) {
                    const updated = response
                    updated.employee = employeeList.find(e => String(e.id) === String(values.employee))
                    updated.vehicle = vehicleList.find(v => String(v.id) === String(values.vehicle))
                    props.onUpdate(updated)
                }

                if (props.closeModal) props.closeModal()
            }
        } catch (error) {
            setErrorMessages(handlePostError(error, 'Inspection'))
        }
    }

    if (redirectPath) return <Navigate to={redirectPath} />

    return (
        <Container>
            <Row>
                {errorMessages.length > 0 && (
                    <Alert variant="danger">
                        <ul>{errorMessages.map((m, i) => <li key={i}>{m}</li>)}</ul>
                    </Alert>
                )}
                {showSuccess && (
                    <Alert variant="success" onClose={() => setShowSuccess(false)} dismissible>
                        Inspection successfully {isUpdating ? 'updated' : 'submitted'}!
                    </Alert>
                )}
            </Row>
            <Row>
                <Formik<InspectionFormValues>
                    enableReinitialize
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={(values, helpers) => {
                        const touchedFields = Object.keys((validationSchema as any).fields).reduce(
                            (acc, f) => ({ ...acc, [f]: true }), {}
                        )
                        helpers.setTouched(touchedFields)
                        helpers.validateForm().then(errors => {
                            if (Object.keys(errors).length === 0) handleSubmit(values)
                        })
                    }}
                    validateOnChange
                    validateOnBlur
                >
                    {formik => (
                        <Form onSubmit={formik.handleSubmit} autoComplete="off" noValidate>
                            <Tabs defaultActiveKey="details" id="inspection-form" className="mb-3" justify>

                                {/* ── Tab 1: Vehicle & Odometer ── */}
                                <Tab eventKey="details" title="Vehicle & Odometer">

                                    {/* Inspection Date — full row, first */}
                                    <Row className="mb-3">
                                        <Form.Group as={Col} xs={12} controlId="inspection_date">
                                            <Form.Label>Inspection Date <span className="text-danger">*</span></Form.Label>
                                            <DatePicker
                                                id="inspection_date"
                                                name="inspection_date"
                                                className="datetime-select form-control"
                                                wrapperClassName="d-block"
                                                dateFormat="dd-MM-yy, h:mm aa"
                                                showTimeSelect
                                                placeholderText="dd-mm-yy hh:mm"
                                                timeIntervals={15}
                                                selected={formik.values.inspection_date ? new Date(formik.values.inspection_date) : null}
                                                onChange={(date: Date) => {
                                                    if (date) formik.setFieldValue('inspection_date', format(date, 'yyyy-MM-dd HH:mm:ss'))
                                                }}
                                                onFocus={e => e.target.blur()}
                                            />
                                            <ErrorMessage name="inspection_date" component="div" className="text-danger" />
                                        </Form.Group>
                                    </Row>

                                    {/* Vehicle — full row */}
                                    <Row className="mb-3">
                                        <Form.Group as={Col} xs={12} controlId="vehicleSelect">
                                            <Form.Label>Vehicle <span className="text-danger">*</span></Form.Label>
                                            <Select
                                                ref={vehicleSelectRef}
                                                name="vehicle"
                                                defaultValue={
                                                    props.inspectionToUpdate?.vehicle
                                                        ? { label: `${props.inspectionToUpdate.vehicle.make} ${props.inspectionToUpdate.vehicle.model} - ${props.inspectionToUpdate.vehicle.vin}`, value: props.inspectionToUpdate.vehicle.id }
                                                        : undefined
                                                }
                                                options={vehicleList.map(v => ({
                                                    label: `${v.make} ${v.model} - ${v.vin}`,
                                                    value: v.id,
                                                })).sort((a, b) => a.label.localeCompare(b.label))}
                                                isClearable={false}
                                                onChange={(opt: any) => formik.setFieldValue('vehicle', opt ? opt.value : '')}
                                                isDisabled={isUpdating}
                                            />
                                            <ErrorMessage name="vehicle" component="div" className="text-danger" />
                                        </Form.Group>
                                    </Row>

                                    {/* Odometer start & end — side by side */}
                                    <Row className="mb-3">
                                        <Form.Group as={Col} controlId="odometer_start">
                                            <Form.Label>Odometer Start <span className="text-danger">*</span></Form.Label>
                                            <Field
                                                name="odometer_start"
                                                placeholder="200000"
                                                as={Form.Control}
                                                value={odoStart}
                                                onChange={(e: any) => {
                                                    const v = parseFloat(e.target.value)
                                                    if (!isNaN(v)) {
                                                        setOdoStart(v.toString())
                                                        formik.setFieldValue('odometer_start', v.toString())
                                                    } else setOdoStart('')
                                                }}
                                            />
                                            <ErrorMessage name="odometer_start" component="div" className="text-danger" />
                                        </Form.Group>
                                        <Form.Group as={Col} controlId="odometer_end">
                                            <Form.Label>Odometer End <span className="text-danger">*</span></Form.Label>
                                            <Field
                                                name="odometer_end"
                                                placeholder="200000"
                                                as={Form.Control}
                                                value={odoEnd}
                                                onChange={(e: any) => {
                                                    const v = parseFloat(e.target.value)
                                                    if (!isNaN(v)) {
                                                        setOdoEnd(v.toString())
                                                        formik.setFieldValue('odometer_end', v.toString())
                                                    } else setOdoEnd('')
                                                }}
                                            />
                                            <ErrorMessage name="odometer_end" component="div" className="text-danger" />
                                        </Form.Group>
                                    </Row>
                                </Tab>

                                {/* ── Tab 2: Inspection Details ── */}
                                <Tab eventKey="inspection" title="Inspection Details">
                                    <Row className="mx-3">
                                        <Col><Form.Label className="fw-bold">Fluid Leaks</Form.Label></Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_leaks_underneath_vehicle">
                                                <Select
                                                    placeholder="Fluid Leaks"
                                                    ref={fluidSelectRef}
                                                    name="pre_job_inspection_leaks_underneath_vehicle"
                                                    defaultValue={
                                                        props.inspectionToUpdate?.pre_job_inspection_leaks_underneath_vehicle
                                                            ? { label: props.inspectionToUpdate.pre_job_inspection_leaks_underneath_vehicle, value: props.inspectionToUpdate.pre_job_inspection_leaks_underneath_vehicle }
                                                            : { label: 'none', value: 'none' }
                                                    }
                                                    options={InspectionVehicleLeakArray.map(e => ({ label: e, value: e })).sort((a, b) => a.label.localeCompare(b.label))}
                                                    onChange={(opt: any) => {
                                                        if (opt) formik.setFieldValue('pre_job_inspection_leaks_underneath_vehicle', opt.value)
                                                    }}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <hr />
                                    <p className="fw-bold">Fluid Levels</p>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_oil">
                                                <Field type="checkbox" name="pre_job_inspection_oil" as={Form.Check} label="Oil Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_oil', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_antifreeze">
                                                <Field type="checkbox" name="pre_job_inspection_antifreeze" as={Form.Check} label="Antifreeze Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_antifreeze', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_power_steering">
                                                <Field type="checkbox" name="pre_job_inspection_power_steering" as={Form.Check} label="Power Steering Fluid Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_power_steering', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_brake">
                                                <Field type="checkbox" name="pre_job_inspection_brake" as={Form.Check} label="Brake Fluid Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_brake', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_windshield_washer">
                                                <Field type="checkbox" name="pre_job_inspection_windshield_washer" as={Form.Check} label="Washer Fluid Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_windshield_washer', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <hr />
                                    <p className="fw-bold">Exterior Inspection</p>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_tires_proper_inflation">
                                                <Field type="checkbox" name="pre_job_inspection_tires_proper_inflation" as={Form.Check} label="Tire Inflation Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_tires_proper_inflation', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_tires_adaquate_tread">
                                                <Field type="checkbox" name="pre_job_inspection_tires_adaquate_tread" as={Form.Check} label="Tire Tread Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_tires_adaquate_tread', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_headlights">
                                                <Field type="checkbox" name="pre_job_inspection_headlights" as={Form.Check} label="Headlights Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_headlights', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_brakelights">
                                                <Field type="checkbox" name="pre_job_inspection_brakelights" as={Form.Check} label="Brakelights Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_brakelights', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_turnlights">
                                                <Field type="checkbox" name="pre_job_inspection_turnlights" as={Form.Check} label="Turn Signals Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_turnlights', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_hazzardlights">
                                                <Field type="checkbox" name="pre_job_inspection_hazzardlights" as={Form.Check} label="Hazard Lights Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_hazzardlights', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_firstaidkit">
                                                <Field type="checkbox" name="pre_job_inspection_firstaidkit" as={Form.Check} label="First Aid Kit Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_firstaidkit', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_fireextinguisher">
                                                <Field type="checkbox" name="pre_job_inspection_fireextinguisher" as={Form.Check} label="Fire Extinguisher Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_fireextinguisher', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_reflectors">
                                                <Field type="checkbox" name="pre_job_inspection_reflectors" as={Form.Check} label="Reflectors Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_reflectors', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_reflectivetriangles">
                                                <Field type="checkbox" name="pre_job_inspection_reflectivetriangles" as={Form.Check} label="Reflective Triangles Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_reflectivetriangles', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_air_system_inspected_and_bled">
                                                <Field type="checkbox" name="pre_job_inspection_air_system_inspected_and_bled" as={Form.Check} label="Air System Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_air_system_inspected_and_bled', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_frame_and_suspension_system">
                                                <Field type="checkbox" name="pre_job_inspection_frame_and_suspension_system" as={Form.Check} label="Frame / Suspension Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_frame_and_suspension_system', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <hr />
                                    <p className="fw-bold">Cab & Safety Equipment Inspection</p>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_fuel_system">
                                                <Field type="checkbox" name="pre_job_inspection_fuel_system" as={Form.Check} label="Fuel System Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_fuel_system', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_temperature">
                                                <Field type="checkbox" name="pre_job_inspection_temperature" as={Form.Check} label="Temperature Sensor Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_temperature', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_seat_belts">
                                                <Field type="checkbox" name="pre_job_inspection_seat_belts" as={Form.Check} label="Seatbelts Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_seat_belts', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_two_way_radio_in_working_order">
                                                <Field type="checkbox" name="pre_job_inspection_two_way_radio_in_working_order" as={Form.Check} label="Two Way Radio Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_two_way_radio_in_working_order', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_noise">
                                                <Field type="checkbox" name="pre_job_inspection_noise" as={Form.Check} label="Vehicle Noises Normal"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_noise', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_dashboard_warning_light">
                                                <Field type="checkbox" name="pre_job_inspection_dashboard_warning_light" as={Form.Check} label="Dashboard Warning Lights Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_dashboard_warning_light', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_windshield_wipers">
                                                <Field type="checkbox" name="pre_job_inspection_windshield_wipers" as={Form.Check} label="Windshield Wipers Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_windshield_wipers', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_heater_and_defroster">
                                                <Field type="checkbox" name="pre_job_inspection_heater_and_defroster" as={Form.Check} label="Heater / Defroster Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_heater_and_defroster', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_mirrors">
                                                <Field type="checkbox" name="pre_job_inspection_mirrors" as={Form.Check} label="Mirrors Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_mirrors', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_windshield_and_perimeter_windows">
                                                <Field type="checkbox" name="pre_job_inspection_windshield_and_perimeter_windows" as={Form.Check} label="Windshield / Perimeter Windows Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_windshield_and_perimeter_windows', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_horn">
                                                <Field type="checkbox" name="pre_job_inspection_horn" as={Form.Check} label="Horn Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_horn', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_exhaust_system">
                                                <Field type="checkbox" name="pre_job_inspection_exhaust_system" as={Form.Check} label="Exhaust Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_exhaust_system', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="pre_job_inspection_brakes_including_parking_brake">
                                                <Field type="checkbox" name="pre_job_inspection_brakes_including_parking_brake" as={Form.Check} label="Brakes (Incl. Parking Brake) Checked & Good"
                                                    onChange={(e: any) => formik.setFieldValue('pre_job_inspection_brakes_including_parking_brake', e.target.checked)} />
                                            </Form.Group>
                                        </Col>
                                        <Col />
                                    </Row>
                                </Tab>

                                {/* ── Tab 3: Safety ── */}
                                <Tab eventKey="safety" title="Safety">
                                    <p className="fw-bold">Safety Checklist</p>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="emergency_contact_uptodate">
                                                <Field type="checkbox" name="pre_job_inspection_emergency_contact_info_upto_date" as={Form.Check}
                                                    label="Emergency Contact Up to Date" />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="ppe_worn">
                                                <Field type="checkbox" name="pre_job_safety_proper_ppe_worn" as={Form.Check}
                                                    label="Proper PPE Worn" />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="truck_safety_discussed">
                                                <Field type="checkbox" name="pre_job_safety_standard_truck_safety_discussed" as={Form.Check}
                                                    label="Truck Safety Discussed" />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="liftgate_operation_discussed">
                                                <Field type="checkbox" name="pre_job_safety_standard_liftgate_operation_discussed" as={Form.Check}
                                                    label="Liftgate Operation Discussed" />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group controlId="loading_dock_and_building_discussed">
                                                <Field type="checkbox" name="pre_job_safety_standard_loading_dock_and_building_discussed" as={Form.Check}
                                                    label="Loading Dock & Building Safety Discussed" />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="equipment">
                                                <Field type="checkbox" name="pre_job_safety_standard_equipment_and_secure_strapment" as={Form.Check}
                                                    label="Equipment & Securement Straps Inspected & Working" />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Form.Group controlId="safety_comments">
                                            <Row className="mb-3">
                                                <Form.Label className="fw-bold">Safety Comments:</Form.Label>
                                                <Field
                                                    style={{ height: '100px' }}
                                                    name="pre_job_safety_comments"
                                                    component="textarea"
                                                    className="form-control"
                                                    placeholder="Add any comments regarding safety here..."
                                                    as={Form.Control}
                                                />
                                            </Row>
                                        </Form.Group>
                                    </Row>
                                </Tab>

                                {/* ── Tab 4: Review ── */}
                                <Tab eventKey="review" title="Review">
                                    <Row className="mb-3">
                                        <Col>
                                            {(formik.errors.employee || formik.errors.vehicle || formik.errors.inspection_date || formik.errors.odometer_start || formik.errors.odometer_end) ? (
                                                <Alert variant="warning">
                                                    <h5>Missing Required Fields:</h5>
                                                    <ul>
                                                        {formik.errors.employee && <li>{formik.errors.employee as string}</li>}
                                                        {formik.errors.vehicle && <li>{formik.errors.vehicle as string}</li>}
                                                        {formik.errors.inspection_date && <li>{formik.errors.inspection_date as string}</li>}
                                                        {formik.errors.odometer_start && <li>{formik.errors.odometer_start as string}</li>}
                                                        {formik.errors.odometer_end && <li>{formik.errors.odometer_end as string}</li>}
                                                    </ul>
                                                </Alert>
                                            ) : (
                                                <Alert variant="success">All required fields have been filled out.</Alert>
                                            )}
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <h4>Components Requiring Attention</h4>
                                            <ul>
                                                {(() => {
                                                    const items = getComponentsRequiringAttention(formik.values)
                                                    return items.length === 0
                                                        ? <li>No Components Require Attention</li>
                                                        : items.map((c, i) => <li key={i}>{c}</li>)
                                                })()}
                                            </ul>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Form.Label className="fw-bold">Vehicle Inspection Comments:</Form.Label>
                                        <Field
                                            style={{ height: '100px' }}
                                            name="pre_job_inspection_comments"
                                            component="textarea"
                                            className="form-control"
                                            placeholder="Add any additional details here"
                                            as={Form.Control}
                                        />
                                    </Row>
                                    <hr />
                                    <Row className="mb-3">
                                        <Form.Group as={Col} controlId="conditionSelect">
                                            <Form.Label>Vehicle Condition Following Inspection</Form.Label>
                                            <Select
                                                ref={conditionSelectRef}
                                                name="pre_job_inspection_vehicle_condition_following_inspection"
                                                options={InspectionVehicleConditionArray.map(e => ({ label: e, value: e })).sort((a, b) => a.label.localeCompare(b.label))}
                                                onChange={(opt: any) => {
                                                    if (opt) formik.setFieldValue('pre_job_inspection_vehicle_condition_following_inspection', opt.value)
                                                }}
                                                defaultValue={
                                                    props.inspectionToUpdate?.pre_job_inspection_vehicle_condition_following_inspection
                                                        ? { label: props.inspectionToUpdate.pre_job_inspection_vehicle_condition_following_inspection, value: props.inspectionToUpdate.pre_job_inspection_vehicle_condition_following_inspection }
                                                        : { label: 'acceptable', value: 'acceptable' }
                                                }
                                            />
                                        </Form.Group>
                                    </Row>
                                    <hr />
                                    <Row className="mb-3">
                                        <Form.Group controlId="information_is_true">
                                            <Form.Check
                                                type="checkbox"
                                                label="I Declare that the vehicle shown above has been inspected in accordance with the applicable requirement of schedule 1 and 2 or jurisdiction legislation."
                                                onChange={() => setInspectionCompletedChecked(!inspectionCompletedChecked)}
                                                checked={inspectionCompletedChecked}
                                            />
                                        </Form.Group>
                                    </Row>
                                    <Row className="justify-content-center mb-3" xs={5}>
                                        <Button variant="primary" type="submit" disabled={!inspectionCompletedChecked || formik.isSubmitting}>
                                            {isUpdating ? 'Update Inspection' : 'Submit Inspection'}
                                        </Button>
                                    </Row>
                                </Tab>

                            </Tabs>
                        </Form>
                    )}
                </Formik>
            </Row>
        </Container>
    )
}

export default CreateInspectionEntry
